import React from "react";
import {useGlobalContext} from "../store/appContext";
import axios from "axios";
import './CustomerBookingsTable.scss';
import dayjs from 'dayjs'
import {Booking, BookingPayment} from '../store/types'
import Button from '@mui/material/Button'
import * as XLSX from 'xlsx'

axios.defaults.withCredentials = true

export default function CustomerBookingsList() {

    const {calendarBookings, selectedObject} = useGlobalContext()

    let bookingsAndRentPaymentsCombined: (Booking | BookingPayment)[] = []
    calendarBookings
        .filter(b => !b.ownerReservation)
        .forEach(b => {
            if (b.bookingPayments?.length) {
                b.bookingPayments.forEach(bp => {
                    bookingsAndRentPaymentsCombined.push({
                        ...bp,
                        startDay: dayjs(bp.dateStart),
                        endDay: dayjs(bp.dateEnd),
                        ownerFeePaidDay: bp.ownerFeePaidDate ? dayjs(bp.ownerFeePaidDate) : null,

                        customerName: b.customerName,
                        customerCountry: b.customerCountry,
                        customerDocumentNumber: b.customerDocumentNumber,
                        customerDateOfBirth: b.customerDateOfBirth,
                        visitors: b.visitors,
                    })
                })
            } else if (dayjs(b.dateEnd).isBefore(dayjs(), 'day')) {
                bookingsAndRentPaymentsCombined.push(b)
            }
    })

    bookingsAndRentPaymentsCombined = bookingsAndRentPaymentsCombined
        .sort((a, b) => dayjs(b.dateStart).diff(dayjs(a.dateStart)))

    // bookingPayments (monthly-split sub-periods) don't carry their own "nights"
    // from the API — only the parent booking does. Compute from the period's
    // own dates instead of trusting `.nights`, so it's correct for both.
    const getNights = (b: Booking | BookingPayment) => b.endDay?.diff(b.startDay, 'day') ?? b.nights

    const exportToExcel = () => {
        const rows = bookingsAndRentPaymentsCombined.map(b => ({
            Period: `${b.startDay?.format('DD.MM.YYYY')} - ${b.endDay?.format('DD.MM.YYYY')}`,
            Nights: getNights(b) ?? '',
            'Customer name': b.customerName || '',
            Country: b.customerCountry || '',
            'Document number': b.customerDocumentNumber || '',
            'Date of birth': b.customerDateOfBirth ? dayjs(b.customerDateOfBirth).format('DD.MM.YYYY') : '',
            Guests: b.visitors ?? '',
            'Rent (€)': b.rentFee ?? '',
            'CSR fee (€)': b.csrRentFee ?? '',
            'Owner fee (€)': b.ownerFee ?? '',
            'Paid to owner': b.ownerFeePaidDay?.format('DD.MM.YYYY') || '',
        }))
        const ws = XLSX.utils.json_to_sheet(rows)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Payments')
        const objectSlug = selectedObject?.objectName?.trim().replace(/\s+/g, '-') || ''
        XLSX.writeFile(wb, objectSlug ? `payments-${objectSlug}.xlsx` : 'payments.xlsx')
    }

    return (
        <div>
            <div style={{ textAlign: 'right', padding: '8px 16px' }}>
                <Button variant="outlined" size="small" onClick={exportToExcel}>
                    Export to Excel
                </Button>
            </div>
            <table className="customer-bookings" style={{
                marginBottom: '180px',
                minWidth: '1200px',
            }}>
                <thead>
                <tr>
                    <th>&nbsp;</th>
                    <th className={'left'}>Period</th>
                    <th>Nights</th>
                    <th className={'left'}>Customer name</th>
                    <th className={'left'}>Country</th>
                    <th className={'left'}>Document number</th>
                    <th className={'left'}>Date of birth</th>
                    <th>Guests</th>
                    <th>Rent</th>
                    <th>CSR fee</th>
                    <th>Owner fee</th>
                    <th>Paid to owner</th>
                    <th>&nbsp;</th>
                </tr>
                </thead>
                <tbody>
                    {bookingsAndRentPaymentsCombined.map((bookingOrPayment, index) => (
                        <tr
                            key={bookingOrPayment._id}
                        >
                            <td>&nbsp;</td>
                            <td className={'left'}>{bookingOrPayment.startDay?.format('DD.MM.YYYY')} - {bookingOrPayment.endDay?.format('DD.MM.YYYY')}</td>
                            <td>{getNights(bookingOrPayment) ?? '-'}</td>
                            <td className={'left'}>{bookingOrPayment.customerName}</td>
                            <td className={'left'}>{bookingOrPayment.customerCountry}</td>
                            <td className={'left'}>{bookingOrPayment.customerDocumentNumber}</td>
                            <td className={'left'}>{bookingOrPayment.customerDateOfBirth ? dayjs(bookingOrPayment.customerDateOfBirth).format('DD.MM.YYYY') : '-'}</td>
                            <td>{bookingOrPayment.visitors ?? '-'}</td>
                            <td>{bookingOrPayment.rentFee ? bookingOrPayment.rentFee.toLocaleString() + ' €' : ''}</td>
                            <td>{bookingOrPayment.csrRentFee ? bookingOrPayment.csrRentFee.toLocaleString() + ' €' : ''}</td>
                            <td>{bookingOrPayment.ownerFee ? bookingOrPayment.ownerFee.toLocaleString() + ' €' : ''}</td>
                            <td>{bookingOrPayment.ownerFeePaidDay?.format('DD.MM.YYYY') || '-'}</td>
                            <td>&nbsp;</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
